import React from 'react';
import { modules, MENU_GROUPS } from '../../router/moduleRegistry';
import { DashboardSVG } from '@ui/components';
import { WalletSVG } from '@ui/components/svg/WalletSVG';
import { ROUTES } from '@core/constants/routes/routes';
import enabledPages from '@core/constants/pages/enabledPages';
import { checkPermissions } from '@core/helpers/PermissionsChecker';
import { palette } from '@ui/styles/theme';

/**
 * Builds the sidebar menu out of the module registry plus the shell's own
 * entries.
 *
 * The menu is NOT a mirror of the module list, and that is why it needs this
 * file instead of reading the registry directly:
 *
 * - **Dashboards** is a shell screen: it has a menu entry but no module.
 * - **Products** is a group: it has a menu entry but no module and no route -
 *   it only opens a submenu.
 * - **Cards** is a module with no menu entry (`menu: null` in the registry).
 *
 * So the registry contributes the items that belong to a module, and the two
 * lists below contribute what does not.
 */

/**
 * Entries that belong to the shell, not to any module. Same shape as a
 * module's `menu`, plus the `id` the registry would have provided.
 */
const SHELL_ITEMS = [
    {
        id: 'dashboards',
        group: null,
        order: 10,
        labelKey: 'menu.dashboards',
        path: ROUTES.DASHBOARD,
        matchPath: ROUTES.DASHBOARD,
        icon: (stroke) => <DashboardSVG customStroke={stroke} strokeWidth='1.3' />,
        permission: 'show_dashboards.main',
        featureFlag: 'enableDashboards',
    },
];

/**
 * Groups gather several modules under one icon. They never navigate anywhere:
 * clicking does nothing, hovering opens the submenu.
 *
 * A group is only shown when at least one of its children survives filtering.
 */
const GROUPS = [
    {
        id: MENU_GROUPS.PRODUCTS,
        order: 30,
        labelKey: 'menu.products',
        matchPath: ROUTES.PRODUCTS.BASE,
        icon: (stroke) => <WalletSVG style={{ stroke, height: '1.846rem', width: '1.846rem', strokeWidth: '1.3' }} />,
        permission: 'show_products.show_links.main',
        featureFlag: null,
    },
];

const isFlagEnabled = (featureFlag) => (featureFlag ? Boolean(enabledPages[featureFlag]) : true);

const isAllowed = (permission) => (permission ? checkPermissions(permission) : true);

const byOrder = (a, b) => a.order - b.order;

/** Every entry that can appear in the menu: the modules that declare one, plus the shell's. */
const collectEntries = () => [
    ...modules.filter(module => module.menu).map(module => ({ id: module.id, ...module.menu })),
    ...SHELL_ITEMS,
];

/**
 * @param {object}   params
 * @param {Function} params.t           i18n translator
 * @param {string}   params.currentPath current location, to mark the active item
 * @param {Function} params.onNavigate  called with the target path on click
 * @returns {Array} items ready to render, already filtered and ordered
 */
export function buildMenu ({ t, currentPath, onNavigate }) {
    const entries = collectEntries()
        .filter(entry => isFlagEnabled(entry.featureFlag));

    const decorate = (entry) => ({
        ...entry,
        label: t(entry.labelKey),
        active: currentPath.startsWith(entry.matchPath),
    });

    const topLevel = entries
        .filter(entry => !entry.group && isAllowed(entry.permission))
        .map(entry => ({
            ...decorate(entry),
            hasSubmenu: false,
            icon: entry.icon(palette.icons),
            iconHover: entry.icon(palette.white),
            onClick: () => onNavigate(entry.path),
        }));

    const groups = GROUPS
        .filter(group => isFlagEnabled(group.featureFlag) && isAllowed(group.permission))
        .map(group => {
            // Children are not permission checked on their own: the group's
            // permission already gates the whole submenu.
            const submenu = entries
                .filter(entry => entry.group === group.id)
                .sort(byOrder)
                .map(entry => {
                    const item = decorate(entry);

                    return {
                        ...item,
                        icon: entry.icon(item.active ? palette.white : palette.icons),
                        onClick: () => onNavigate(entry.path),
                    };
                });

            return {
                ...decorate(group),
                hasSubmenu: true,
                submenu,
                icon: group.icon(currentPath.startsWith(group.matchPath) ? palette.white : palette.icons),
                iconHover: group.icon(palette.white),
            };
        })
        .filter(group => group.submenu.length > 0);

    return [...topLevel, ...groups].sort(byOrder);
}

export default buildMenu;
